<template>
  <v-container>
    <h1>Lending Organizations</h1>
    <hr />

    <OrganizationsTable
      :loading="loading"
      :organizations="organizations"
      :confirmingToggle="!!showConfirmToggleDialog"
      @removedUser="removeUserFromOrganization"
      @toggleHidden="startTogglingHidden"
      @startCreateEdit="startCreateEdit" />

    <v-dialog
      v-if="showCreateEdit" :value="true"
      max-width="600"
      @click:outside="showCreateEdit = null">
      <CreateEditOrganization
        :editing="showCreateEdit.editing"

        @cancel="showCreateEdit = null"
        @save="showCreateEdit.editing ? onUpdate($event) : onCreate($event)"
      />
    </v-dialog>

    <v-dialog
      v-if="showConfirmRemoveDialog" :value="true"
      max-width="400"
      @click:outside="showConfirmRemoveDialog = null">
      <v-card>
        <v-card-title><h2>Confirm</h2></v-card-title>
        <v-card-text>
          Are you sure you want to remove this user from this organization? This will remove them from the organization and set their role back to 'user'.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <button
            class="default-btn text"
            @click="showConfirmRemoveDialog = false"
          >Cancel</button>

          <button
            class="default-btn red"
            @click="showConfirmRemoveDialog.callback"
          >Remove</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="showConfirmToggleDialog" :value="true"
      max-width="400"
      @click:outside="showConfirmToggleDialog = null">
      <ConfirmToggleHidden
        :hidden="showConfirmToggleDialog.hidden"
        @cancel="showConfirmToggleDialog = null"
        @confirm="toggleOrganizationHiddenStatus"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import ConfirmToggleHidden from './ConfirmToggleHidden'
import CreateEditOrganization from './CreateEditOrganization'
import OrganizationsTable from './OrganizationsTable'

export default {
  name: 'AdminOrganizations',
  components: {
    ConfirmToggleHidden,
    CreateEditOrganization,
    OrganizationsTable,
  },
  data: () => ({
    loading: true,
    organizations: [],
    showCreateEdit: null,
    showConfirmRemoveDialog: null,
    showConfirmToggleDialog: null,
  }),
  computed: {
    ...mapGetters('feathers', ['apiOrganizations', 'apiUsers']),
  },

  async created () {
    try {
      this.organizations = await this.apiOrganizations.find({
        query: {
          $include: {
            ProductCount: true,
            UserCount: true,
            Users: true,
          },
        },
      })
    } catch (error) {
      this.addAlert({
        type: 'error',
        message: `Error when getting organizations: ${error.message}`,
      })
    }

    this.loading = false
  },

  methods: {
    startCreateEdit (index = -1) {
      this.showCreateEdit = index > -1
        ? {
          editing: true,
          existing: this.organizations[index],
          index,
        }
        : { editing: false }
    },

    async onCreate (organization) {
      try {
        this.organizations.push(await this.apiOrganizations.create(organization))
        this.showCreateEdit = null
      } catch (error) {
        this.addAlert({
          type: 'error',
          message: `Could not create: ${error.message}`,
        })
      }
    },

    async onUpdate (organization) {
      try {
        const updated = await this.apiOrganizations.patch(organization.id, organization)
        this.$set(this.organizations, this.showCreateEdit.index, updated)
        this.showCreateEdit = null
      } catch (error) {
        this.addAlert({
          type: 'error',
          message: `Could not create: ${error.message}`,
        })
      }
    },

    async removeUserFromOrganization ({ organizationId, userId }, confirmed = false) {
      if (!confirmed) {
        this.showConfirmRemoveDialog = {
          callback: () => this.removeUserFromOrganization({ organizationId, userId }, true),
        }
        return
      }

      this.showConfirmRemoveDialog = null

      const organizationIndex = this.organizations.findIndex(({ id }) => {
        return id === organizationId
      })

      const userIndex = this.organizations[organizationIndex].users.findIndex(({ id }) => {
        return id === userId
      })

      if (organizationIndex === -1 || userIndex === -1) return

      try {
        await this.apiUsers.patch(userId, {
          organizationId: null,
          role: 'user',
        })

        this.organizations[organizationIndex].users.splice(userIndex, 1)
        this.organizations[organizationIndex].userCount -= 1
        this.addSuccessAlert('Removed the user')
      } catch (error) {
        this.addErrorAlert(`Could not remove the user: ${error.message}`)
      }
    },

    startTogglingHidden (item) {
      this.showConfirmToggleDialog = {
        hidden: item.hidden,
        id: item.id,
        index: this.organizations.findIndex(org => org === item),
      }
    },

    async toggleOrganizationHiddenStatus () {
      const { id, index, hidden } = this.showConfirmToggleDialog

      try {
        await this.apiOrganizations.patch(id, { hidden: !hidden })

        this.$set(this.organizations[index], 'hidden', !hidden)
        this.showConfirmToggleDialog = null
        this.addSuccessAlert('Successfully changed the visiblity of the organization.')
      } catch (error) {
        this.addErrorAlert(`Could not change the visibility: ${error.message}`)
      }
    },
  },
}
</script>

<style lang="scss" scoped>

</style>
